import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef
} from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Paper,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { BRAND_COLOR, BRAND_HOVER } from "@kivunova/kivufrontendcommon";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useCreateStore } from "../../hooks/api/useStore";
import { AddStoreModalProps, StoreFormData } from "./types/storeType";
import {
  useRwandaLocation,
  useKivunovaTranslation,
  PhoneCountryCodeMap,
  combinePhoneNumber,
  isValidPhoneNumberForCountry,
  getDefaultCountryCode
} from "@kivunova/kivufrontendcommon";
import { CountryCode } from "libphonenumber-js";

const DEFAULT_ADDRESS = {
  country: "Rwanda",
  province: "",
  district: "",
  sector: "",
  cell: "",
  village: "",
  streetAddress: ""
};

const AddStoreModal: React.FC<AddStoreModalProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useKivunovaTranslation();
  const { userDetails } = useSelector((state: RootState) => state.user);
  const createStore = useCreateStore();
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const openedRef = useRef(false);

  const {
    getProvinces,
    getDistrictByProvince,
    getSectors,
    getCells,
    getVillages,
    loading: locationLoading
  } = useRwandaLocation();

  // Contact info state
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState(() =>
    getDefaultCountryCode()
  );
  const [phoneError, setPhoneError] = useState("");

  const schema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().required(
          t("store-name-required", "Store name is required")
        ),
        companyId: Yup.string().required(),
        address: Yup.object({
          country: Yup.string().required(
            t("store-country-required", "Country is required")
          ),
          province: Yup.string().required(
            t("store-province-required", "Province is required")
          ),
          district: Yup.string().required(
            t("store-district-required", "District is required")
          ),
          sector: Yup.string().required(
            t("store-sector-required", "Sector is required")
          ),
          cell: Yup.string().required(
            t("store-cell-required", "Cell is required")
          ),
          village: Yup.string().required(
            t("store-village-required", "Village is required")
          ),
          streetAddress: Yup.string().required(
            t("store-street-required", "Street address is required")
          )
        })
      }),
    [t]
  );

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<StoreFormData>({
    resolver: yupResolver(schema),
    mode: "onChange",
    shouldFocusError: true,
    defaultValues: {
      name: "",
      companyId: userDetails?.companyId || "",
      address: { ...DEFAULT_ADDRESS }
    }
  });

  useEffect(() => {
    if (!open) {
      openedRef.current = false;
      return;
    }

    if (userDetails?.companyId) {
      setValue("companyId", userDetails.companyId);
    }

    if (openedRef.current) {
      return;
    }

    openedRef.current = true;
    reset({
      name: "",
      companyId: userDetails?.companyId || "",
      address: { ...DEFAULT_ADDRESS }
    });
    setEmail("");
    setPhoneNumber("");
    setPhoneCountryCode(getDefaultCountryCode());
    setPhoneError("");
  }, [open, userDetails?.companyId, reset, setValue]);

  // Watch cascading fields
  const province = watch("address.province");
  const district = watch("address.district");
  const sector = watch("address.sector");
  const cell = watch("address.cell");

  // Location Data
  const provinces = useMemo(() => getProvinces(), [getProvinces]);
  const districts = useMemo(
    () => (province ? getDistrictByProvince(province) : []),
    [province, getDistrictByProvince]
  );
  const sectors = useMemo(
    () => (district ? getSectors(province, district) : []),
    [province, district, getSectors]
  );
  const cells = useMemo(
    () => (sector ? getCells(province, district, sector) : []),
    [province, district, sector, getCells]
  );
  const villages = useMemo(
    () => (cell ? getVillages(province, district, sector, cell) : []),
    [province, district, sector, cell, getVillages]
  );

  // Phone validation
  const validatePhone = useCallback(
    (number: string, countryCode: string) => {
      if (!number || !countryCode) {
        setPhoneError(t("phone-required", "Phone number is required"));
        return false;
      }

      const countryCodeValue =
        PhoneCountryCodeMap[countryCode]?.code || countryCode;
      const countryKey = Object.keys(PhoneCountryCodeMap).find(
        (key) => PhoneCountryCodeMap[key].code === countryCodeValue
      );

      if (!countryKey) {
        setPhoneError(t("invalid-country-code", "Invalid country code"));
        return false;
      }

      const countryCodeForValidation = countryKey as CountryCode;
      const isValid = isValidPhoneNumberForCountry(
        number,
        countryCodeForValidation,
        countryCodeValue
      );

      if (!isValid) {
        setPhoneError(
          t("invalid-phone", "Invalid phone number for selected country")
        );
        return false;
      }

      setPhoneError("");
      return true;
    },
    [t]
  );

  const handlePhoneNumberChange = (value: string) => {
    setPhoneNumber(value);
    if (value) {
      validatePhone(value, phoneCountryCode);
    } else {
      setPhoneError("");
    }
  };

  const handlePhoneCountryCodeChange = (value: string) => {
    setPhoneCountryCode(value);
    if (phoneNumber) {
      validatePhone(phoneNumber, value);
    }
  };

  // Reset cascading levels
  const onProvinceChange = useCallback(
    (value: string) => {
      setValue("address.province", value);
      setValue("address.district", "");
      setValue("address.sector", "");
      setValue("address.cell", "");
      setValue("address.village", "");
    },
    [setValue]
  );

  const onDistrictChange = useCallback(
    (value: string) => {
      setValue("address.district", value);
      setValue("address.sector", "");
      setValue("address.cell", "");
      setValue("address.village", "");
    },
    [setValue]
  );

  const onSectorChange = useCallback(
    (value: string) => {
      setValue("address.sector", value);
      setValue("address.cell", "");
      setValue("address.village", "");
    },
    [setValue]
  );

  const onCellChange = useCallback(
    (value: string) => {
      setValue("address.cell", value);
      setValue("address.village", "");
    },
    [setValue]
  );

  const scrollToFormTop = useCallback(() => {
    const el = modalBodyRef.current;
    if (!el) {
      return;
    }
    if (typeof el.scrollTo === "function") {
      el.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      el.scrollTop = 0;
    }
  }, []);

  const onSubmit = async (data: StoreFormData) => {
    if (!data.companyId?.trim()) {
      scrollToFormTop();
      return;
    }

    // Validate phone before submitting
    if (phoneNumber && !validatePhone(phoneNumber, phoneCountryCode)) {
      scrollToFormTop();
      return;
    }

    const fullPhoneNumber = combinePhoneNumber(
      phoneCountryCode,
      phoneNumber,
      PhoneCountryCodeMap
    );

    const payload = {
      ...data,
      contactEmail: email || undefined,
      contactPhone: fullPhoneNumber || undefined
    };

    try {
      await createStore.mutateAsync(payload);
      reset();
      setEmail("");
      setPhoneNumber("");
      setPhoneCountryCode(getDefaultCountryCode());
      setPhoneError("");
      onClose();
    } catch {
      /* alerts via mutation + fetchGraphQL */
    }
  };

  const onInvalid = useCallback(() => {
    scrollToFormTop();
  }, [scrollToFormTop]);

  const isLoading = isSubmitting || createStore.isPending;

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="modal-styles">
        {/* ── modal-header: fixed top — title + close button ── */}
        <Box className="modal-header">
          <Typography variant="h5" fontWeight="bold" color="textPrimary">
            {t("store-add-title", "Add Store")}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* ── modal-body: the only scrolling region ── */}
        <Box
          ref={modalBodyRef}
          component="form"
          id="add-store-form"
          onSubmit={handleSubmit(onSubmit, onInvalid)}
          display="flex"
          flexDirection="column"
          gap={2}
          className="modal-body"
        >
          {/* Store Name */}
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t("store-name-label", "Store Name")}
                error={!!errors.name}
                helperText={errors.name?.message}
                size="small"
              />
            )}
          />

          {/* Contact Information */}
          <Paper
            variant="outlined"
            sx={{
              p: matchMD ? 1.5 : 2,
              display: "flex",
              flexDirection: "column",
              gap: matchMD ? 1.5 : 2
            }}
          >
            <Typography
              variant={matchMD ? "body1" : "subtitle1"}
              fontWeight={600}
              color="textPrimary"
            >
              {t("contact-information", "Contact Information")}
            </Typography>

            <Box display="flex" flexDirection="column" gap={matchMD ? 1.5 : 2}>
              <TextField
                label={t("email", "Email")}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                size="small"
              />

              {/* Phone Number with Country Code */}
              <Box
                sx={{
                  display: "flex",
                  gap: matchMD ? 1 : 2,
                  alignItems: "flex-start",
                  flexDirection: matchMD ? "column" : "row"
                }}
              >
                <FormControl
                  sx={{
                    flex: matchMD ? 1 : "0 0 160px",
                    minWidth: matchMD ? "100%" : 140,
                    width: matchMD ? "100%" : "auto"
                  }}
                  variant="outlined"
                  error={!!phoneError}
                  disabled={isLoading}
                  size="small"
                >
                  <InputLabel id="phone-country-code-label">
                    {t("country-code", "Code")}
                  </InputLabel>
                  <Select
                    value={phoneCountryCode || getDefaultCountryCode()}
                    onChange={(e) =>
                      handlePhoneCountryCodeChange(e.target.value)
                    }
                    labelId="phone-country-code-label"
                    id="phone-country-code"
                    label={t("country-code", "Code")}
                    disabled={isLoading}
                  >
                    {Object.keys(PhoneCountryCodeMap).map((key) => (
                      <MenuItem key={key} value={key}>
                        {`${PhoneCountryCodeMap[key].flag} ${PhoneCountryCodeMap[key].code}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label={t("phone-number", "Phone Number")}
                  value={phoneNumber}
                  onChange={(e) => handlePhoneNumberChange(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!phoneError}
                  helperText={phoneError}
                  placeholder="782484464"
                  disabled={isLoading}
                />
              </Box>
            </Box>
          </Paper>

          {/* Rwanda Address Section */}
          <Paper
            variant="outlined"
            sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              {t("store-address-section-title", "Store Address (Rwanda)")}
            </Typography>

            {/* Country */}
            <Controller
              name="address.country"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t("store-field-country", "Country")}
                  disabled
                  error={!!errors.address?.country}
                  helperText={errors.address?.country?.message}
                  size="small"
                />
              )}
            />

            {/* Province */}
            <Controller
              name="address.province"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  error={!!errors.address?.province}
                  size="small"
                >
                  <InputLabel>
                    {t("store-field-province", "Province")}
                  </InputLabel>
                  <Select
                    {...field}
                    label={t("store-field-province", "Province")}
                    disabled={locationLoading}
                    onChange={(e) => {
                      field.onChange(e);
                      onProvinceChange(e.target.value);
                    }}
                  >
                    {provinces.map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors.address?.province?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />

            {/* District */}
            {province && (
              <Controller
                name="address.district"
                control={control}
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    error={!!errors.address?.district}
                    size="small"
                  >
                    <InputLabel>
                      {t("store-field-district", "District")}
                    </InputLabel>
                    <Select
                      {...field}
                      label={t("store-field-district", "District")}
                      onChange={(e) => {
                        field.onChange(e);
                        onDistrictChange(e.target.value);
                      }}
                    >
                      {districts.map((d) => (
                        <MenuItem key={d} value={d}>
                          {d}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {errors.address?.district?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            )}

            {/* Sector */}
            {district && (
              <Controller
                name="address.sector"
                control={control}
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    error={!!errors.address?.sector}
                    size="small"
                  >
                    <InputLabel>{t("store-field-sector", "Sector")}</InputLabel>
                    <Select
                      {...field}
                      label={t("store-field-sector", "Sector")}
                      onChange={(e) => {
                        field.onChange(e);
                        onSectorChange(e.target.value);
                      }}
                    >
                      {sectors.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {errors.address?.sector?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            )}

            {/* Cell */}
            {sector && (
              <Controller
                name="address.cell"
                control={control}
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    error={!!errors.address?.cell}
                    size="small"
                  >
                    <InputLabel>{t("store-field-cell", "Cell")}</InputLabel>
                    <Select
                      {...field}
                      label={t("store-field-cell", "Cell")}
                      onChange={(e) => {
                        field.onChange(e);
                        onCellChange(e.target.value);
                      }}
                    >
                      {cells.map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {errors.address?.cell?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            )}

            {/* Village */}
            {cell && (
              <Controller
                name="address.village"
                control={control}
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    error={!!errors.address?.village}
                    size="small"
                  >
                    <InputLabel>
                      {t("store-field-village", "Village")}
                    </InputLabel>
                    <Select
                      {...field}
                      label={t("store-field-village", "Village")}
                    >
                      {villages.map((v) => (
                        <MenuItem key={v} value={v}>
                          {v}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {errors.address?.village?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            )}

            {/* Street Address */}
            {cell && (
              <Controller
                name="address.streetAddress"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t("store-field-street", "Street Address")}
                    error={!!errors.address?.streetAddress}
                    helperText={errors.address?.streetAddress?.message}
                    size="small"
                  />
                )}
              />
            )}
          </Paper>
        </Box>

        {/* ── modal-footer: fixed bottom, flex-shrink:0 ── */}
        <Box className="modal-footer">
          <LoadingButton
            data-testid="add-store-submit-btn"
            onClick={handleSubmit(onSubmit, onInvalid)}
            fullWidth
            variant="contained"
            loading={isLoading}
            sx={{
              backgroundColor: BRAND_COLOR,
              "&:hover": { backgroundColor: BRAND_HOVER }
            }}
          >
            {t("store-add-submit", "Add Store")}
          </LoadingButton>

          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            sx={{
              borderColor: BRAND_COLOR,
              color: BRAND_COLOR,
              "&:hover": {
                backgroundColor: BRAND_HOVER,
                color: "white",
                borderColor: BRAND_HOVER
              }
            }}
          >
            {t("cancel", "Cancel")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddStoreModal;
